import { action } from '@ember/object';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type { SelectOption } from 'donor-elf-web/lib/form-component-utils';
import MailchimpAccountModel from 'donor-elf-web/models/mailchimp-account';
import type { MailchimpFundSyncInfo } from 'donor-elf-web/routes/funds/fund/more/mailchimp-sync';
import Changeset from 'ember-changeset';
import lookupValidator from 'ember-changeset-validations';
import validateMailchimpFundSync from 'donor-elf-web/validations/mailchimp-fund-sync';
import { service } from '@ember/service';
import CurrentUser from 'donor-elf-web/services/current-user';
import FundsService from 'donor-elf-web/services/funds-service';
import { sortByProperties } from 'donor-elf-web/lib/array-utils';
import Store from '@ember-data/store';
import Settings from 'donor-elf-web/services/settings';

interface ComponentArgs {
  mailchimpAccounts: MailchimpAccountModel[];
  mailchimpFundSyncInfo: MailchimpFundSyncInfo;
  savedMailchimpFundSyncInfos: MailchimpFundSyncInfo[];
  onSaved: (mailchimpFundSyncInfo: MailchimpFundSyncInfo) => void;
  onCancel: () => void;
}

type AutomaticallySyncType = 'none' | 'first' | 'all';

export default class MailchimpSyncFundSyncIndexComponent extends Component<ComponentArgs> {
  constructor(owner: any, args: ComponentArgs) {
    super(owner, args);
    this.createChangeset();
    if (this.newRecord && this.currentUser.hasMultipleFunds) {
      this.loadFundOptions();
    }
  }
  @service declare currentUser: CurrentUser;
  @service declare fundsService: FundsService;
  @service declare settings: Settings;
  @service declare store: Store;

  @tracked changeset: any;
  @tracked loadingFunds = false;
  @tracked fundOptions?: SelectOption[];
  @tracked saving = false;
  @tracked showValidationErrors = false;
  @tracked error: any;
  connecting = false;

  get mailchimpFundSync() {
    return this.args.mailchimpFundSyncInfo.mailchimpFundSync;
  }

  get newRecord(): boolean {
    return this.mailchimpFundSync.isNew as unknown as boolean;
  }

  get audienceNotSet() {
    return this.args.mailchimpFundSyncInfo.mailchimpAudience == null;
  }

  get audienceOptions(): SelectOption[] {
    let { mailchimpAccount } = this;
    if (mailchimpAccount == null) {
      return [];
    }
    return mailchimpAccount.audiences.map((x) => {
      return {
        val: x.id,
        label: x.name,
      };
    });
  }

  get audience() {
    return this.args.mailchimpAccounts[0].audiences.find(
      (x) => x.id === this.mailchimpFundSync.audienceId,
    );
  }

  // based on underlying record
  get mailchimpAccountNotSet() {
    return this.mailchimpFundSync.mailchimpAccountId == null;
  }

  // based on changeset
  get mailchimpAccountSelected() {
    return this.changeset.get('mailchimpAccountId') != null;
  }

  get mailchimpAccountOptions(): SelectOption[] {
    let options = this.args.mailchimpAccounts.map((x) => {
      return {
        val: x.id,
        label: x.accountName || 'Not Set',
      };
    });
    if (this.args.savedMailchimpFundSyncInfos.length > 0) {
      options.push({ val: 'new', label: 'Add new MailChimp Account' });
    }
    return options;
  }

  get mailchimpAccount() {
    return this.args.mailchimpAccounts.find(
      (x) => x.id === this.changeset.get('mailchimpAccountId'),
    );
  }

  @action
  async handleMailchimpAccountIdChanged(val: string) {
    if (val === 'new') {
      let signInModel = this.store.createRecord('devise-sign-in');
      await signInModel.save();
      window.location.href = '/auth/mailchimp';
    } else {
      let mailchimpAccount = this.args.mailchimpAccounts.find(
        (x) => x.id === val,
      )!;
      if (mailchimpAccount.audiences.length === 1) {
        this.changeset.set('audienceId', mailchimpAccount.audiences[0].id);
      }
    }
  }

  @action
  async handleConnectToOtherMailchimpAccount() {
    if (this.connecting) {
      return;
    }
    this.connecting = true;
    try {
      let signInModel = this.store.createRecord('devise-sign-in');
      await signInModel.save();
      window.location.href = '/auth/mailchimp';
    } catch (e) {
      this.error = e;
    }
  }

  get showDropdownToSyncGroupsToInterestCategory() {
    return this.mailchimpFundSync.syncGroupsToInterestCategory;
  }

  get automaticSyncTypeOptions(): SelectOption[] {
    return [
      {
        val: 'none',
        label: 'Disabled',
      },
      {
        val: 'first',
        label: 'First Email',
      },
      {
        val: 'all',
        label: 'All Emails',
      },
    ];
  }

  get booleanOptions(): SelectOption[] {
    return [
      {
        val: true,
        label: 'Yes',
      },
      {
        val: false,
        label: 'No',
      },
    ];
  }

  createChangeset() {
    let { mailchimpFundSync } = this;
    if ((mailchimpFundSync as any).isNew) {
      mailchimpFundSync.automaticallySyncFirstEmail = true;
      mailchimpFundSync.syncGroupsToTags = true;
    }
    let automaticSyncType: AutomaticallySyncType = 'none';
    if (mailchimpFundSync.automaticallySyncFirstEmail) {
      automaticSyncType = 'first';
    } else if (mailchimpFundSync.automaticallySyncAllEmails) {
      automaticSyncType = 'all';
    }
    (mailchimpFundSync as any).automaticSyncType = automaticSyncType;
    let validation = validateMailchimpFundSync({
      allMailchimpFundSyncs: this.args.savedMailchimpFundSyncInfos.map(
        (x) => x.mailchimpFundSync,
      ),
    });
    this.changeset = new Changeset(
      mailchimpFundSync,
      lookupValidator(validation),
      validation,
    );
  }

  async loadFundOptions() {
    this.loadingFunds = true;
    let funds = await this.fundsService.getAllFunds();
    this.fundOptions = sortByProperties(
      funds.map((fund) => {
        return {
          val: fund.id,
          label: fund.name,
        };
      }),
      ['label'],
    );
    this.loadingFunds = false;
  }

  @action
  async save() {
    if (!this.newRecord && this.changeset.isPristine) {
      this.args.onCancel();
      return;
    }
    this.saving = true;
    await this.changeset.validate();
    if (this.changeset.get('isValid')) {
      this.showValidationErrors = false;
      this.changeset.execute();
      let { mailchimpFundSync } = this;
      let automaticSyncType = this.changeset.get(
        'automaticSyncType',
      ) as AutomaticallySyncType;
      if (automaticSyncType === 'all') {
        mailchimpFundSync.automaticallySyncFirstEmail = false;
        mailchimpFundSync.automaticallySyncAllEmails = true;
      } else if (automaticSyncType === 'first') {
        mailchimpFundSync.automaticallySyncFirstEmail = true;
        mailchimpFundSync.automaticallySyncAllEmails = false;
      } else {
        mailchimpFundSync.automaticallySyncFirstEmail = false;
        mailchimpFundSync.automaticallySyncAllEmails = false;
      }
      try {
        await mailchimpFundSync.save();
        // have to update settings.mailchimpFundSyncSlim
        this.settings.unload();
        await this.settings.loadSettings(this.fundsService.selectedFund!.id);
        this.saving = false;
        this.args.mailchimpFundSyncInfo.mailchimpAccount =
          this.mailchimpAccount;
        this.args.mailchimpFundSyncInfo.mailchimpAudience =
          this.mailchimpAccount?.audiences.find(
            (x) => x.id === mailchimpFundSync.audienceId,
          );
        if (
          this.args.mailchimpFundSyncInfo.fund?.id !== mailchimpFundSync.fundId
        ) {
          let allFunds = await this.fundsService.getAllFunds();
          this.args.mailchimpFundSyncInfo.fund = allFunds.find(
            (x) => x.id == mailchimpFundSync.fundId,
          );
        }
        this.args.onSaved(this.args.mailchimpFundSyncInfo);
      } catch (e) {
        this.error = e;
      }
    } else {
      this.saving = false;
      this.showValidationErrors = true;
    }
  }
}
